import { Button } from '@/components/common/Button';
import { Checkbox } from '@/components/common/Checkbox';
import Combobox from '@/components/common/Combobox';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormMessage
} from '@/components/common/Form';
import { Input } from '@/components/common/Input';
import { Label } from '@/components/common/Label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/common/Popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/common/Select';
import { Textarea } from '@/components/common/Textarea';
import { PHONE_VALIDATE_REGEX, SHOP_CATEGORY, REPRESENTATIVE_ROLE, SHOP_STATUS, COORDINATE_REGEX } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { INFO_TYPE, LANGUAGES_SUPPORTED, REPRESENTATIVE_ROLE_TYPE, SHOP_STATUS_TYPE } from '@/types/enums';
import { GetGeneralInfoResponse } from '@/types/http-payload/shop';
import { toastify } from '@/utils/toastify';
import { removeVietnameseAccents, scrollToTop } from '@/utils/utils';
import { yupResolver } from '@hookform/resolvers/yup';
import debounce from 'lodash.debounce';
import { MapPinIcon } from 'lucide-react';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import EditShopFormFooter from '../EditShopFormFooter';
import { v4 as uuidv4 } from 'uuid';
import { ThreeDots } from 'react-loader-spinner';
import { MultiSelect } from '@/components/common/MultiSelect';

export default observer(function GeneralInformationTab() {
    // hooks
    const { t, i18n: { language } } = useTranslation();
    const { code } = useParams();
    const sessionToken = uuidv4();

    // store
    const {
        generalStore: { administrativeUnit, getPlaceAutoComplete },
        shopStore: { shopInfo, getShopInfo, updateGeneralInfo }
    } = useStore();

    // validate schema
    const validateSchema = yup.object().shape({
        name: yup
            .string()
            .required('form_error_validate.required'),
        address: yup
            .string()
            .required('form_error_validate.required'),
        provinceName: yup
            .string()
            .required('form_error_validate.required'),
        provinceCode: yup
            .string()
            .required('form_error_validate.required'),
        categoryIds: yup
            .array()
            .of(yup.number().required('form_error_validate.required'))
            .min(1, 'form_error_validate.required'),
        taxCode: yup
            .string()
            .required('form_error_validate.required')
            .max(13, 'form_error_validate.tax_code_maxlength'),
        phoneNumber: yup
            .string()
            .required('form_error_validate.required'),
        zaloId: yup
            .string(),
        // owner
        ownerName: yup
            .string()
            .required('form_error_validate.required'),
        ownerPhoneNumber: yup
            .string()
            .required('form_error_validate.required'),
        // representative
        representativeName: yup
            .string()
            .required('form_error_validate.required'),
        representativePhoneNumber: yup
            .string()
            .required('form_error_validate.required'),
        representativeRole: yup
            .string()
            .required('form_error_validate.required'),
        representativeEmail: yup
            .string()
            .email('form_error_validate.format_email'),
        status: yup
            .string(),
        reason: yup
            .string()
            .when('status', {
                is: status => status === SHOP_STATUS_TYPE.Inactive.toString() || status === SHOP_STATUS_TYPE.DeniedApprove.toString(),
                then: _ => yup.string().required('form_error_validate.required'),
                otherwise: _ => yup.string().nullable().optional()
            })
    });

    type FormData = yup.InferType<typeof validateSchema>;

    const initialValues = {
        name: '',
        address: '',
        provinceName: '',
        provinceCode: '',
        categoryIds: [],
        taxCode: '',
        phoneNumber: '',
        zaloId: '',
        ownerName: '',
        ownerPhoneNumber: '',
        representativeName: '',
        representativePhoneNumber: '',
        representativeRole: '',
        representativeEmail: ''
    };

    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onChange',
        defaultValues: initialValues
    });

    const iframeFindPlaceRef = useRef<HTMLIFrameElement>(null);

    // state
    const [isOwnerAlsoPresentative, setIsOwnerAlsoPresentative] = useState<boolean>(false);
    const [placesSearch, setPlacesSearch] = useState<any[]>([]);
    const [openPopoverSearch, setOpenPopoverSearch] = useState<boolean>(false);
    const [showIframeFindPlace, setShowIframeFindPlace] = useState<boolean>(false);
    const [loadingIframeFindPlace, setLoadingIframeFindPlace] = useState<boolean>(true);
    const [coordinate, setCoordinate] = useState<string>('');
    const [placeId, setPlaceId] = useState<string>('');
    const [mapUrl, setMapUrl] = useState<string>('');

    // lifecycle
    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        if (shopInfo) {
            const { name, address, province, categoryIds, taxCode, phoneNumber, zaloId, ownerName, ownerPhoneNumber, representativeName, representativePhoneNumber, representativeRole, representativeEmail, coordinate, placeId, status, reason } = shopInfo as GetGeneralInfoResponse;
            form.reset({
                name: name ?? '',
                address: address ?? '',
                provinceCode: province?.code ?? '',
                provinceName: province?.nameEn,
                categoryIds: categoryIds ?? [],
                taxCode: taxCode ?? '',
                phoneNumber: phoneNumber ?? '',
                zaloId: zaloId ?? '',
                ownerName: ownerName ?? '',
                ownerPhoneNumber: ownerPhoneNumber ? ownerPhoneNumber : phoneNumber ?? '',
                representativeName: representativeName ?? '',
                representativePhoneNumber: representativePhoneNumber ?? '',
                representativeRole: representativeRole ? representativeRole.toString() : '',
                representativeEmail: representativeEmail ?? '',
                status: status.toString() ?? '',
                reason: reason ?? ''
            });
            setCoordinate(coordinate ?? '');
            setPlaceId(placeId ?? '');
        }
    }, [shopInfo]);

    useEffect(() => {
        if (placesSearch?.length > 0) {
            setOpenPopoverSearch(true);
        }
    }, [placesSearch]);

    useEffect(() => {
        if (placeId) {
            handleOnChangeMap(placeId);
        }
    }, [placeId]);

    // function
    const fetchData = async () => {
        await flowResult(getShopInfo(code ?? '', { typeInfo: INFO_TYPE.GeneralInfo }));
    };

    const onSubmit = async (data) => {
        const { name, address, provinceCode, categoryIds, taxCode, phoneNumber, zaloId, ownerName, ownerPhoneNumber, representativeName, representativePhoneNumber, representativeRole, representativeEmail, status, reason } = data;

        const res = await flowResult(updateGeneralInfo(code ?? '', {
            name, address, provinceCode,
            taxCode, phoneNumber, zaloId, ownerName, ownerPhoneNumber,
            representativeName, representativePhoneNumber,
            categoryIds: categoryIds,
            representativeRole: Number(representativeRole),
            representativeEmail,
            placeId,
            status,
            reason,
            sessionToken
        }));
        if (res) {
            fetchData();
            toastify('success', t('messages.update_success'));
            scrollToTop();
        }
    };

    const onHandleClickCheckbox = () => {
        const provinceCodeCurrent = form.getValues('provinceCode');
        if (!isOwnerAlsoPresentative) {
            const ownerNameCurrent = form.getValues('ownerName');
            const phoneNumberOwnerCurrent = form.getValues('ownerPhoneNumber');
            form.reset({
                ...form.getValues(),
                representativeName: ownerNameCurrent,
                representativePhoneNumber: phoneNumberOwnerCurrent,
                representativeRole: String(REPRESENTATIVE_ROLE_TYPE.ShopOwner),
                provinceCode: provinceCodeCurrent
            });
        }
        form.trigger(['ownerName', 'ownerPhoneNumber', 'representativeName', 'representativePhoneNumber']);
        setIsOwnerAlsoPresentative(!isOwnerAlsoPresentative);
    };

    const onGetPlaceAutocomplete = async (addr: string) => {
        let data = [];
        try {
            if (addr.trim()) {
                const response = await flowResult(getPlaceAutoComplete({ input: addr.trim(), sessionToken }));
                data = response;
            }
        } catch (error) {
        } finally {
            setPlacesSearch(data);
        }
    };

    const onDebounceGetPlaceAutocomplete = useCallback(
        debounce((addr: string) => {
            onGetPlaceAutocomplete(addr);
        }, 500),
        []
    );

    const handleLoad = () => {
        setLoadingIframeFindPlace(false);
    };

    const handleOnChangeMap = (placeId) => {
        let initGoogleMap = 'https://www.google.com/maps/embed/v1/';
        if (placeId) {
            initGoogleMap += `place?q=place_id:${placeId}`;
        } else {
            initGoogleMap += 'view?center=21.0285,105.8542&zoom=12&maptype=roadmap';
        }
        initGoogleMap += `&language=${language}&key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`;
        setMapUrl(initGoogleMap);
    };

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className='my-4'>
                <div className='bg-white rounded p-4 mt-4'>
                    <FormField
                        control={form.control}
                        name='name'
                        render={({ field }) => (
                            <FormItem>
                                <Label className='label-1' required>{t('words_title.shop_name')}</Label>
                                <FormControl className='mt-1.5'>
                                    <Input type='text' {...field} />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name='address'
                        render={({ field }) => (
                            <FormItem className='mt-4'>
                                <Label className='label-1' required>{t('words_title.shop_address')}</Label>
                                <Popover open={openPopoverSearch}>
                                    <PopoverTrigger className='w-full'>
                                        <FormControl className='mt-1.5'>
                                            <Input
                                                value={field.value}
                                                onChange={(e) => {
                                                    field.onChange(e.target.value);
                                                    setPlaceId('');
                                                    setMapUrl('');
                                                    setCoordinate('');
                                                    setShowIframeFindPlace(false);
                                                    onDebounceGetPlaceAutocomplete(field.value);
                                                }}
                                            />
                                        </FormControl>
                                    </PopoverTrigger>
                                    <PopoverContent
                                        className='w-full p-1 !max-w-[900px]'
                                        align='start'
                                        autoFocus={false}
                                        widthFull={true}
                                        onOpenAutoFocus={e => e.preventDefault()}
                                        onPointerDownOutside={() => setOpenPopoverSearch(false)}
                                    >
                                        {
                                            placesSearch?.length > 0 && (
                                                <div className='divide-y divide-gray-200'>
                                                    {
                                                        placesSearch.map((place, index) => (
                                                            <div
                                                                key={index}
                                                                className='py-2 px-1 text-sm flex items-center gap-2 hover:cursor-pointer hover:bg-slate-200'
                                                                onClick={() => {
                                                                    if (place?.address) {
                                                                        const desctructuringAddress = place.address.split(', ');
                                                                        const city = desctructuringAddress[desctructuringAddress.length - 1];
                                                                        const cityAccent = removeVietnameseAccents(city)?.trim();
                                                                        const cityCode = administrativeUnit?.provinces.find(item => item.nameEn === cityAccent ||
                                                                            removeVietnameseAccents(item.fullName) === cityAccent)?.code;
                                                                        if (cityCode) {
                                                                            form.setValue('provinceName', cityAccent);
                                                                            form.setValue('provinceCode', cityCode);
                                                                            form.clearErrors('provinceName');
                                                                            form.clearErrors('provinceCode');
                                                                        }
                                                                        form.setValue('address', place.address);
                                                                    }
                                                                    setPlaceId(place?.placeId);
                                                                    setOpenPopoverSearch(false);
                                                                }}
                                                            >
                                                                <MapPinIcon className='w-4 h-4' />
                                                                <span>{place?.address}</span>
                                                            </div>
                                                        ))
                                                    }
                                                </div>
                                            )
                                        }
                                    </PopoverContent>
                                </Popover>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <div className='flex md:flex-row flex-col md:gap-4 gap-0'>
                        <FormField
                            control={form.control}
                            name='provinceName'
                            render={({ field }) => (
                                <FormItem className='flex flex-col mt-4 md:w-1/2 w-full'>
                                    <Label className='label-1' required>{t('words_title.city')}</Label>
                                    <FormControl className='mt-1.5'>
                                        <Combobox
                                            placeholder={t('placeholder.choose')}
                                            value={field.value}
                                            onChange={(item) => {
                                                field.onChange(item?.value);
                                                form.setValue('provinceCode', item?.code);
                                            }}
                                            items={administrativeUnit?.provinces?.map((item, index) => (
                                                {
                                                    label: language === LANGUAGES_SUPPORTED.English ? item.fullNameEn : item.fullName,
                                                    value: item?.nameEn,
                                                    code: item?.code
                                                }
                                            ))}
                                            required
                                            side='top'
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <div className='md:w-1/2 w-full'></div>
                    </div>

                    <div>
                        <div className='flex gap-4 bg-background-2 rounded px-4 py-6 mt-4'>
                            <FormItem className='w-1/2'>
                                <div className='flex flex-col'>
                                    <Label className='label-1'>{t('words_title.coordinate')}</Label>
                                    <Input className='mt-1.5' value={coordinate} disabled />
                                </div>
                                <FormMessage />
                            </FormItem>
                            <FormItem className='w-1/2'>
                                <div className='flex flex-col'>
                                    <Label className='label-1'>{t('words_title.place_id')}</Label>
                                    <div className='flex items-center gap-4 mt-1.5'>
                                        <Input value={placeId} disabled />
                                        <Button
                                            type='button'
                                            className=''
                                            variant='outline'
                                            onClick={() => {
                                                setShowIframeFindPlace(!showIframeFindPlace);
                                                setLoadingIframeFindPlace(true);
                                            }}
                                        >
                                            {showIframeFindPlace ? t('button.cancel') : t('button.get_id')}
                                        </Button>
                                    </div>
                                </div>
                                <FormMessage />
                            </FormItem>
                        </div>
                        {
                            showIframeFindPlace && (
                                <div className='relative w-full h-[600px]'>
                                    {loadingIframeFindPlace && (
                                        <ThreeDots
                                            wrapperClass='absolute inset-0 flex justify-center items-center'
                                            visible={true}
                                            height='80'
                                            width='80'
                                            color='#9A6051'
                                            radius='9'
                                            ariaLabel='three-dots-loading'
                                        />
                                    )}
                                    <iframe
                                        ref={iframeFindPlaceRef}
                                        title='embed-maps-find-place'
                                        width='100%'
                                        height='600'
                                        loading='lazy'
                                        referrerPolicy='no-referrer-when-downgrade'
                                        allowFullScreen
                                        allow='geolocation'
                                        src={mapUrl}
                                        onLoad={handleLoad}
                                    />
                                </div>
                            )
                        }
                    </div>

                    <div className='flex md:flex-row flex-col md:gap-4 gap-0'>
                        <FormField
                            control={form.control}
                            name='status'
                            render={({ field }) => (
                                <FormItem className='flex flex-col mt-4 md:w-1/2 w-full'>
                                    <Label className='label-1'>{t('words_title.status')}</Label>
                                    <FormControl>
                                        <Select
                                            key={field.value}
                                            onValueChange={field.onChange}
                                            {...field}
                                            disabled
                                        >
                                            <SelectTrigger className='mt-1.5'>
                                                <SelectValue placeholder={t('placeholder.choose')}>
                                                </SelectValue>
                                            </SelectTrigger>
                                            <SelectContent>
                                                {Object.keys(SHOP_STATUS).map(item => (
                                                    <SelectItem
                                                        className='hover:cursor-pointer'
                                                        key={SHOP_STATUS[item].key}
                                                        value={item}
                                                    >
                                                        <span>{t(`words_title.${SHOP_STATUS[item].key}`)}</span>
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        {
                            (form.getValues('status') === SHOP_STATUS_TYPE.Inactive.toString() || form.getValues('status') === SHOP_STATUS_TYPE.DeniedApprove.toString()) ?
                                (
                                    <FormField
                                        control={form.control}
                                        name='reason'
                                        render={({ field }) => (
                                            <FormItem className='flex flex-col mt-4 md:w-1/2 w-full'>
                                                <Label className='label-1' required>{t('words_title.reason')}</Label>
                                                <FormControl className='mt-1.5'>
                                                    <Textarea
                                                        maxLength={1000}
                                                        className='min-h-36'
                                                        {...field}
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                ) :
                                (
                                    <div className='md:w-1/2 w-full'></div>
                                )
                        }
                    </div>
                    <div className='flex md:flex-row flex-col md:gap-4 gap-0'>
                        <FormField
                            control={form.control}
                            name='categoryIds'
                            render={({ field, fieldState }) => (
                                <FormItem className='flex flex-col mt-4 md:w-1/2 w-full'>
                                    <Label className='label-1 mb-[6px]' required>
                                        {t('words_title.category')}
                                    </Label>
                                    <FormControl>
                                        <MultiSelect
                                            options={Object.keys(SHOP_CATEGORY).map(key => ({
                                                label: t(`select_options.${SHOP_CATEGORY[key].key}`),
                                                value: Number(key)
                                            }))}
                                            fieldState={fieldState}
                                            value={field.value || []}
                                            onChange={selectedValues => field.onChange(selectedValues)}
                                            placeholder=''
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name='taxCode'
                            render={({ field }) => (
                                <FormItem className='flex flex-col mt-4 md:w-1/2 w-full'>
                                    <Label className='label-1' required>{t('words_title.tax_code')}</Label>
                                    <FormControl className='mt-1.5'>
                                        <Input
                                            maxLength={13}
                                            type='number'
                                            isTypingOnlyNumber={true}
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>
                    <div className='flex md:flex-row flex-col md:gap-4 gap-0'>
                        <FormField
                            control={form.control}
                            name='phoneNumber'
                            render={({ field }) => (
                                <FormItem className='flex flex-col mt-4 md:w-1/2 w-full'>
                                    <Label className='label-1' required>{t('words_title.phone_number')}</Label>
                                    <FormControl className='mt-1.5'>
                                        <Input
                                            maxLength={10}
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name='zaloId'
                            render={({ field }) => (
                                <FormItem className='flex flex-col mt-4 md:w-1/2 w-full'>
                                    <Label className='label-1'>{t('words_title.zalo_id')}</Label>
                                    <FormControl className='mt-1.5'>
                                        <Input
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>
                </div>
                <div className='bg-white mt-4 rounded p-4'>
                    <div className='flex md:flex-row flex-col gap-4'>
                        <FormField
                            control={form.control}
                            name='ownerName'
                            render={({ field }) => (
                                <FormItem className='flex flex-col md:w-1/2 w-full'>
                                    <Label className='label-1' required>{t('words_title.owner_name')}</Label>
                                    <FormControl className='mt-1.5'>
                                        <Input
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name='ownerPhoneNumber'
                            render={({ field }) => (
                                <FormItem className='flex flex-col md:w-1/2 w-full'>
                                    <Label className='label-1' required>{t('words_title.phone_number')}</Label>
                                    <FormControl className='mt-1.5'>
                                        <Input
                                            maxLength={10}
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>
                    <div className='flex items-center mt-4'>
                        <Checkbox
                            checked={isOwnerAlsoPresentative}
                            onCheckedChange={onHandleClickCheckbox}
                        />
                        <Label className='label-1 text-[0.8125rem] ml-2 mt-0.5'>{t('sentences.general_tab_check_box')}</Label>
                    </div>
                </div>
                <div className='bg-white mt-4 rounded p-4'>
                    <div className='flex md:flex-row flex-col gap-4'>
                        <FormField
                            control={form.control}
                            name='representativeName'
                            render={({ field }) => (
                                <FormItem className='flex flex-col md:w-1/2 w-full'>
                                    <Label className='label-1' required>{t('words_title.representative_name')}</Label>
                                    <FormControl className='mt-1.5'>
                                        <Input
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name='representativePhoneNumber'
                            render={({ field }) => (
                                <FormItem className='flex flex-col md:w-1/2 w-full'>
                                    <Label className='label-1' required>{t('words_title.phone_number')}</Label>
                                    <FormControl className='mt-1.5'>
                                        <Input
                                            maxLength={10}
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>
                    <div className='flex md:flex-row flex-col md:gap-4 gap-0'>
                        <FormField
                            control={form.control}
                            name='representativeRole'
                            render={({ field }) => (
                                <FormItem className='flex flex-col mt-4 md:w-1/2 w-full'>
                                    <Label className='label-1' required>{t('words_title.role')}</Label>
                                    <FormControl>
                                        <Select
                                            key={field.value}
                                            onValueChange={field.onChange}
                                            {...field}
                                        >
                                            <SelectTrigger className='mt-1.5'>
                                                <SelectValue placeholder={t('placeholder.choose')} />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {Object.keys(REPRESENTATIVE_ROLE).map(item => (
                                                    <SelectItem
                                                        className='hover:cursor-pointer'
                                                        key={REPRESENTATIVE_ROLE[item].key}
                                                        value={item}
                                                    >
                                                        <span>{t(`select_options.${REPRESENTATIVE_ROLE[item].key}`)}</span>
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                        <FormField
                            control={form.control}
                            name='representativeEmail'
                            render={({ field }) => (
                                <FormItem className='flex flex-col mt-4 md:w-1/2 w-full'>
                                    <Label className='label-1'>{t('words_title.email')}</Label>
                                    <FormControl className='mt-1.5'>
                                        <Input
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>
                </div>
                <EditShopFormFooter code={code ?? ''} onSubmit={form.handleSubmit(onSubmit)} isSubmitDisabled={form.formState.isSubmitting} form={form} fetchData={fetchData} />
            </form>
        </Form>
    );
});
